// pages/hospitals/HospitalFinder.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Grid3X3, List, Map, SlidersHorizontal, MapPin, RefreshCw } from 'lucide-react';
import SearchBar from '../../components/common/SearchBar';
import HospitalFilters from '../../components/common/HospitalFilters';
import HospitalList from '../../components/lists/HospitalList';
import HospitalMap from '../../components/maps/HospitalMap';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import { hospitalService } from '../../services/hospitalService';

const HospitalFinder = () => {
  // State management
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    city: '',
    services: [],
    rating: '',
    radius: 25,
    emergency: false,
    insurance: [],
    minBeds: '',
    hasParking: false,
    wheelchairAccessible: false,
    hasWifi: false,
    hasPharmacy: false
  });
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('distance');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12
  });
  const [error, setError] = useState('');
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(true);
  const [locationDeclined, setLocationDeclined] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Show location prompt on mount (no automatic location request)
  useEffect(() => {
    setInitialLoadComplete(true);
  }, []);

  // Fetch hospitals
  const fetchHospitals = useCallback(async (page = 1) => {
    if (!initialLoadComplete) return;

    try {
      setLoading(true);
      setError('');

      const useNearbyEndpoint = location && filters.radius <= 50;

      if (useNearbyEndpoint) {
        const params = {
          latitude: location.latitude,
          longitude: location.longitude,
          radius: filters.radius,
          limit: pagination.itemsPerPage,
          page: page
        };

        if (debouncedSearch.trim()) params.search = debouncedSearch;
        if (filters.services.length > 0) params.services = filters.services.join(',');
        if (filters.emergency) params.emergency = 'true';

        const response = await hospitalService.getNearbyHospitals(params, true);

        if (response.success) {
          setHospitals(response.data.hospitals || []);
          setPagination({
            currentPage: page,
            totalPages: Math.ceil(response.data.total / pagination.itemsPerPage),
            totalItems: response.data.total,
            itemsPerPage: pagination.itemsPerPage
          });
        } else {
          setError(response.message);
          setHospitals([]);
        }
      } else {
        const params = {
          page,
          limit: pagination.itemsPerPage,
          isActive: 'true'
        };

        if (location) {
          params.latitude = location.latitude;
          params.longitude = location.longitude;
          params.radius = filters.radius;
        } else {
          params.sortBy = sortBy === 'distance' ? 'rating' : sortBy;
          params.sortOrder = sortBy === 'rating' ? 'desc' : 'asc';
        }

        if (debouncedSearch.trim()) params.search = debouncedSearch;
        if (filters.city.trim()) params.city = filters.city;
        if (filters.services.length > 0) params.services = filters.services.join(',');
        if (filters.rating) params.rating = filters.rating;
        if (filters.emergency) params.emergency = 'true';

        const response = await hospitalService.getHospitals(params, true);

        if (response.success) {
          const hospitalsData = response.data.hospitals || [];
          setHospitals(hospitalsData);
          
          if (response.data.pagination) {
            setPagination({
              currentPage: response.data.pagination.currentPage,
              totalPages: response.data.pagination.totalPages,
              totalItems: response.data.pagination.totalItems,
              itemsPerPage: response.data.pagination.itemsPerPage
            });
          } else {
            setPagination(prev => ({
              ...prev,
              currentPage: page,
              totalItems: hospitalsData.length,
              totalPages: Math.ceil(hospitalsData.length / prev.itemsPerPage)
            }));
          }
        } else {
          let errorMessage = response.message || 'Failed to load hospitals';
          if (response.suggestions?.length > 0) {
            errorMessage += '\n\nSuggestions:\n' + response.suggestions.join('\n');
          }
          setError(errorMessage);
          setHospitals([]);
        }
      }
    } catch (err) {
      console.error('Fetch hospitals error:', err);
      setError('Failed to load hospitals. Please try again.');
      setHospitals([]);
    } finally {
      setLoading(false);
    }
  }, [
    location,
    debouncedSearch,
    filters.city,
    filters.services,
    filters.rating,
    filters.emergency,
    filters.radius,
    sortBy,
    pagination.itemsPerPage,
    initialLoadComplete
  ]);

  useEffect(() => {
    if (initialLoadComplete) {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      fetchHospitals(1);
    }
  }, [
    debouncedSearch,
    filters.city,
    filters.services,
    filters.rating,
    filters.emergency,
    filters.radius,
    sortBy,
    initialLoadComplete
  ]);

  // Request location handler
  const getCurrentLocation = useCallback(async () => {
    setLocationLoading(true);
    setLocationError('');
    setError('');
    setShowLocationPrompt(false);
    
    const result = await hospitalService.getCurrentLocation();
    
    if (result.success) {
      setLocation(result.data);
      setLocationDeclined(false);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      
      try {
        const params = {
          latitude: result.data.latitude,
          longitude: result.data.longitude,
          radius: filters.radius,
          limit: pagination.itemsPerPage,
          emergency: filters.emergency
        };
        
        if (filters.services.length > 0) {
          params.services = filters.services.join(',');
        }
        
        const response = await hospitalService.getNearbyHospitals(params, true);
        
        if (response.success) {
          setHospitals(response.data.hospitals || []);
          setPagination(prev => ({
            ...prev,
            totalItems: response.data.total || 0,
            currentPage: 1,
            totalPages: Math.ceil((response.data.total || 0) / prev.itemsPerPage)
          }));
        }
      } catch (error) {
        console.error('Error fetching nearby hospitals:', error);
        fetchHospitals(1);
      }
    } else {
      setLocationDeclined(true);
      setLocationError(result.message);
      setError(`Unable to get your location: ${result.message}`);
      
      if (result.suggestions?.length > 0) {
        setError(prev => prev + '\n\nSuggestions:\n' + result.suggestions.join('\n'));
      }
    }
    
    setLocationLoading(false);
  }, [filters.radius, filters.emergency, filters.services, pagination.itemsPerPage, fetchHospitals]);

  // Update location handler
  const handleUpdateLocation = useCallback(async () => {
    await getCurrentLocation();
  }, [getCurrentLocation]);

  // Decline location handler
  const handleDeclineLocation = useCallback(() => {
    setShowLocationPrompt(false);
    setLocationDeclined(true);
    setLocation(null);
    // Fetch hospitals without location
    fetchHospitals(1);
  }, [fetchHospitals]);

  // Event handlers
  const handleSearch = (query) => setSearchQuery(query);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      services: [],
      rating: '',
      radius: 25,
      emergency: false,
      insurance: [],
      minBeds: '',
      hasParking: false,
      wheelchairAccessible: false,
      hasWifi: false,
      hasPharmacy: false
    });
    setSearchQuery('');
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    fetchHospitals(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleHospitalClick = (hospital) => {
    setSelectedHospital(hospital);
    window.location.href = `/hospitals/${hospital._id}`;
  };

  const handleCallHospital = (phone) => {
    if (phone) window.location.href = `tel:${phone}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Location Prompt Banner */}
      {showLocationPrompt && !location && (
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <MapPin size={24} className="flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">Find Hospitals Near You</h3>
                  <p className="text-blue-100 text-sm">
                    Allow location access to see nearby hospitals sorted by distance and get accurate directions.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <button
                  onClick={getCurrentLocation}
                  disabled={locationLoading}
                  className="px-6 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {locationLoading ? 'Getting Location...' : 'Allow Location'}
                </button>
                <button
                  onClick={handleDeclineLocation}
                  className="px-6 py-2 bg-blue-700 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Find Healthcare Near You</h1>
            <p className="text-lg text-primary-100 max-w-2xl mx-auto">
              Discover quality hospitals and medical facilities in your area with real-time availability and verified information.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-4xl mx-auto">
            <SearchBar
              value={searchQuery}
              onChange={handleSearch}
              onLocationRequest={getCurrentLocation}
              locationLoading={locationLoading}
              placeholder="Search hospitals, medical services, or locations..."
              className="w-full"
            />
          </div>

          {/* Quick Stats */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{pagination.totalItems}+</div>
              <div className="text-primary-100 text-sm">Verified Hospitals</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">24/7</div>
              <div className="text-primary-100 text-sm">Emergency Services</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">1000+</div>
              <div className="text-primary-100 text-sm">Specialist Doctors</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">50+</div>
              <div className="text-primary-100 text-sm">Medical Services</div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            
            {/* Location Info */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              {location ? (
                <>
                  <span className="text-sm text-gray-600 bg-green-50 px-3 py-1 rounded-full flex items-center gap-1">
                    <MapPin size={14} className="text-green-600" />
                    Within {filters.radius}km of your location
                  </span>
                  <button
                    onClick={handleUpdateLocation}
                    disabled={locationLoading}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 disabled:opacity-50"
                    title="Update location"
                  >
                    <RefreshCw size={14} className={locationLoading ? 'animate-spin' : ''} />
                    Update Location
                  </button>
                </>
              ) : locationDeclined ? (
                <>
                  <span className="text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                    ⚠️ Location not enabled - showing all hospitals
                  </span>
                  <button
                    onClick={getCurrentLocation}
                    disabled={locationLoading}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 disabled:opacity-50"
                  >
                    <MapPin size={14} />
                    Enable Location
                  </button>
                </>
              ) : null}
              
              <span className="text-sm text-gray-600">
                {pagination.totalItems} hospitals found
              </span>
            </div>

            {/* View Controls */}
            <div className="flex items-center gap-4 flex-wrap">
              {/* Sort Dropdown */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              >
                {location && <option value="distance">Nearest First</option>}
                <option value="rating">Highest Rated</option>
                <option value="name">Name A-Z</option>
                <option value="createdAt">Recently Added</option>
              </select>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'grid' ? 'bg-white shadow text-primary-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Grid view"
                  aria-label="Grid view"
                >
                  <Grid3X3 size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'list' ? 'bg-white shadow text-primary-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="List view"
                  aria-label="List view"
                >
                  <List size={16} />
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'map' ? 'bg-white shadow text-primary-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Map view"
                  aria-label="Map view"
                >
                  <Map size={16} />
                </button>
              </div>

              {/* Filters Toggle */}
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  showAdvancedFilters ? 'bg-primary-50 border-primary-200 text-primary-700' : 'border-gray-300 hover:bg-gray-50'
                }`}
                aria-label="Toggle filters"
              >
                <SlidersHorizontal size={16} />
                Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <HospitalFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        showAdvancedFilters={showAdvancedFilters}
        onToggleAdvancedFilters={() => setShowAdvancedFilters(!showAdvancedFilters)}
        onClearFilters={clearFilters}
      />

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-700 whitespace-pre-line">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-500 hover:text-red-700 text-xl leading-none"
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {viewMode === 'map' ? (
          <HospitalMap
            hospitals={hospitals}
            center={location || { latitude: -1.2921, longitude: 36.8219 }}
            userLocation={location}
            selectedHospital={selectedHospital}
            onHospitalSelect={setSelectedHospital}
            className="w-full h-[600px]"
            showRadius={true}
            radiusKm={filters.radius}
            showClusters={true}
            showControls={true}
            onRequestLocation={getCurrentLocation}
          />
        ) : loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner 
              size="lg" 
              variant="medical" 
              text={location ? "Finding hospitals near you..." : "Loading hospitals..."} 
            />
          </div>
        ) : hospitals.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Map size={64} className="mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hospitals found</h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your search criteria or filters
            </p>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          <>
            <HospitalList
              hospitals={hospitals}
              loading={loading}
              viewMode={viewMode}
              showDistance={!!location}
              onHospitalClick={handleHospitalClick}
              onCallHospital={handleCallHospital}
            />

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.itemsPerPage}
                  onPageChange={handlePageChange}
                  showInfo={true}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer Stats */}
      <div className="bg-white border-t border-gray-200 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-primary-600 mb-2">{pagination.totalItems}+</div>
              <div className="text-gray-600">Verified Hospitals</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 mb-2">24/7</div>
              <div className="text-gray-600">Emergency Services</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">100+</div>
              <div className="text-gray-600">Specialist Doctors</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">50+</div>
              <div className="text-gray-600">Medical Services</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HospitalFinder;